import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { displayUsername } from '../utils/displayUser';
import {
  PdsBox,
  PdsCard,
  PdsMantineText,
  PdsMantineButton,
  PdsTextField,
  PdsTextArea,
  PdsLoader,
  PdsNotification,
  PdsTag,
  PdsModal,
  PdsModalHeader,
  PdsModalBody,
  PdsModalFooter,
  PdsPlusAddIcon,
  PdsEyePublicViewIcon,
  PdsCloseXIcon,
  PdsCheckConfirmIcon,
  PdsTicketIcon,
  PdsSpinnerIcon,
  PdsFilterIcon,
  PdsSearchIcon,
  PdsLink,
  PdsExternalLinkIcon,
} from '@podium-design-system/react-components';
import CaseCompactActions from '../components/CaseCompactActions';
import Pagination from '../components/Pagination';

const JIRA_BROWSE_BASE = 'https://sportradar.atlassian.net/browse/';

const formatDeriveFromLabel = (df) => {
  if (!df) return '';
  return df.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Clicks on the case row navigate to the case unless they hit an element marked with this attribute (expand, JIRA, Resume, etc.). */
function isCaseCardStopClick(target) {
  return target instanceof Element && target.closest('[data-case-card-stop]') != null;
}

const EMPTY_FILTERS = {
  domain: '',
  jiraId: '',
  keyword: '',
  createdBy: '',
  status: '',
};

const METRIC_SELECTED_STYLE = {
  background: '#21262d',
  borderColor: '#30363d',
};

const STATUS_COLOR = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'neutral',
};

const Cases = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [newCase, setNewCase] = useState({
    title: '',
    description: '',
    target: '',
    jiraId: ''
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [caseStats, setCaseStats] = useState({ active_cases: 0, paused_cases: 0, closed_cases: 0 });
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const navigate = useNavigate();

  useEffect(() => {
    performSearch({ ...EMPTY_FILTERS }, 0);
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performSearch = async (filtersToUse, page = 0) => {
    setLoading(true);
    const cleanFilters = Object.fromEntries(
      Object.entries(filtersToUse).filter(([, v]) => v !== '')
    );
    try {
      const response = await axios.post('/api/cases/search', { ...cleanFilters, includeSubCases: true, page });
      setSearchResults(response.data);
      setCurrentPage(page);
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/cases/stats');
      setCaseStats(response.data);
    } catch (err) {
      console.error('Failed to load case stats:', err);
    }
  };

  const handleCreateCase = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const response = await axios.post('/api/cases', newCase);
      setNewCase({ title: '', description: '', target: '', jiraId: '' });
      setShowCreateModal(false);
      navigate(`/cases/${response.data.id}`);
    } catch (err) {
      console.error('Error creating case:', err);
      setError('Failed to create case: ' + (err.response?.data?.message || err.message));
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString) => {
    if (dateString == null || dateString === '') return '—';
    const d = new Date(dateString);
    return Number.isNaN(d.getTime()) ? String(dateString) : d.toLocaleDateString();
  };

  const handleViewDetails = (caseItem, investigationId = null) => {
    const state = investigationId ? { selectedInvestigationId: investigationId } : {};
    navigate(`/cases/${caseItem.id}`, { state });
  };

  const handleCloseCase = async (caseItem) => {
    try {
      await axios.put(`/api/cases/${caseItem.id}/status`, { status: 'COMPLETED' });
      fetchStats();
      performSearch(filters, currentPage);
    } catch (err) {
      console.error('Error closing case:', err);
      setError('Failed to close case');
    }
  };

  const handlePauseCase = async (caseItem) => {
    try {
      await axios.put(`/api/cases/${caseItem.id}/status`, { status: 'PAUSED' });
      fetchStats();
      performSearch(filters, currentPage);
    } catch (err) {
      console.error('Error pausing case:', err);
      setError('Failed to pause case');
    }
  };

  const handleResumeCase = async (caseItem) => {
    try {
      await axios.put(`/api/cases/${caseItem.id}/status`, { status: 'ACTIVE' });
      fetchStats();
      performSearch(filters, currentPage);
    } catch (err) {
      console.error('Error resuming case:', err);
      setError('Failed to resume case');
    }
  };

  const handleDeleteCase = async (caseItem) => {
    if (!window.confirm(`Are you sure you want to delete the case for "${caseItem.target}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`/api/cases/${caseItem.id}`);
      fetchStats();
      performSearch(filters, currentPage);
      setSuccessMessage(`Case for ${caseItem.target} has been deleted successfully`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error deleting case:', err);
      setError('Failed to delete case: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditCase = (caseItem) => {
    setEditingCase(caseItem);
    setShowEditModal(true);
  };

  const refreshCaseList = () => {
    fetchStats();
    performSearch(filters, currentPage);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(filters, 0);
  };

  const handleMetricCardClick = (statusEnum) => {
    const nextStatus = filters.status === statusEnum ? '' : statusEnum;
    const nextFilters = { ...filters, status: nextStatus };
    setFilters(nextFilters);
    performSearch(nextFilters, 0);
  };

  const handleMetricCardKeyDown = (e, statusEnum) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleMetricCardClick(statusEnum);
    }
  };

  const handleStatusSegmentChange = (nextStatus) => {
    const nextFilters = { ...filters, status: nextStatus };
    setFilters(nextFilters);
    performSearch(nextFilters, 0);
  };

  const clearSearch = () => {
    setFilters({ ...EMPTY_FILTERS });
    performSearch({ ...EMPTY_FILTERS }, 0);
  };

  const metricCardSurfaceStyle = (statusEnum) =>
    filters.status === statusEnum ? METRIC_SELECTED_STYLE : {};

  const statusSegmentStyle = (value) =>
    filters.status === value
      ? { background: '#21262d', color: '#f0f6fc', borderColor: '#30363d' }
      : {};

  if (loading && !searchResults) {
    return (
      <PdsBox justifyContent="center" padding="xl">
        <PdsLoader size="lg" />
      </PdsBox>
    );
  }

  return (
    <PdsBox direction="column" gap="lg">
      <PdsBox direction="row" justifyContent="space-between" alignItems="center">
        <PdsMantineText type="display" fontWeight="strong" className="argus-type-page-heading">
          Cases
        </PdsMantineText>
        <PdsMantineButton
          rank="fill"
          color="action"
          onClick={() => setShowCreateModal(true)}
          leadingIcon={<PdsPlusAddIcon />}
        >
          New Case
        </PdsMantineButton>
      </PdsBox>

      <PdsBox direction="row" gap="md" wrap="wrap">
        <PdsBox
          role="button"
          tabIndex={0}
          aria-pressed={filters.status === 'ACTIVE'}
          aria-label={`Filter by active cases, ${caseStats.active_cases} total`}
          onClick={() => handleMetricCardClick('ACTIVE')}
          onKeyDown={(e) => handleMetricCardKeyDown(e, 'ACTIVE')}
          style={{ cursor: 'pointer' }}
        >
          <PdsCard
            border
            borderRadius="md"
            padding="lg"
            direction="column"
            gap="xs"
            fixedWidth="200px"
            style={metricCardSurfaceStyle('ACTIVE')}
          >
            <PdsMantineText
              type="display"
              fontWeight="strong"
              fontSize="900"
              style={filters.status === 'ACTIVE' ? { color: '#f0f6fc' } : undefined}
            >
              {caseStats.active_cases}
            </PdsMantineText>
            <PdsMantineText
              type="eyebrow"
              color={filters.status === 'ACTIVE' ? undefined : 'neutral'}
              style={filters.status === 'ACTIVE' ? { color: '#8b949e' } : undefined}
            >
              Active Cases
            </PdsMantineText>
          </PdsCard>
        </PdsBox>
        <PdsBox
          role="button"
          tabIndex={0}
          aria-pressed={filters.status === 'PAUSED'}
          aria-label={`Filter by paused cases, ${caseStats.paused_cases} total`}
          onClick={() => handleMetricCardClick('PAUSED')}
          onKeyDown={(e) => handleMetricCardKeyDown(e, 'PAUSED')}
          style={{ cursor: 'pointer' }}
        >
          <PdsCard
            border
            borderRadius="md"
            padding="lg"
            direction="column"
            gap="xs"
            fixedWidth="200px"
            style={metricCardSurfaceStyle('PAUSED')}
          >
            <PdsMantineText
              type="display"
              fontWeight="strong"
              fontSize="900"
              style={filters.status === 'PAUSED' ? { color: '#f0f6fc' } : undefined}
            >
              {caseStats.paused_cases}
            </PdsMantineText>
            <PdsMantineText
              type="eyebrow"
              color={filters.status === 'PAUSED' ? undefined : 'neutral'}
              style={filters.status === 'PAUSED' ? { color: '#8b949e' } : undefined}
            >
              Paused Cases
            </PdsMantineText>
          </PdsCard>
        </PdsBox>
        <PdsBox
          role="button"
          tabIndex={0}
          aria-pressed={filters.status === 'COMPLETED'}
          aria-label={`Filter by completed cases, ${caseStats.closed_cases} total`}
          onClick={() => handleMetricCardClick('COMPLETED')}
          onKeyDown={(e) => handleMetricCardKeyDown(e, 'COMPLETED')}
          style={{ cursor: 'pointer' }}
        >
          <PdsCard
            border
            borderRadius="md"
            padding="lg"
            direction="column"
            gap="xs"
            fixedWidth="200px"
            style={metricCardSurfaceStyle('COMPLETED')}
          >
            <PdsMantineText
              type="display"
              fontWeight="strong"
              fontSize="900"
              style={filters.status === 'COMPLETED' ? { color: '#f0f6fc' } : undefined}
            >
              {caseStats.closed_cases}
            </PdsMantineText>
            <PdsMantineText
              type="eyebrow"
              color={filters.status === 'COMPLETED' ? undefined : 'neutral'}
              style={filters.status === 'COMPLETED' ? { color: '#8b949e' } : undefined}
            >
              Completed Cases
            </PdsMantineText>
          </PdsCard>
        </PdsBox>
      </PdsBox>

      {/* Search */}
      <PdsCard border borderRadius="md" padding="md" direction="column" gap="md">
        <PdsMantineText type="textline" fontWeight="strong">Search</PdsMantineText>
        <form onSubmit={handleSearch}>
          <PdsBox direction="column" gap="md">
            <PdsBox direction="row" gap="md" wrap="wrap" alignItems="flex-end">
              <PdsTextField
                label="Domain / Keyword"
                placeholder="example.com or keyword..."
                value={filters.domain}
                onChange={(e) => setFilters({ ...filters, domain: e.target.value, keyword: e.target.value })}
                fixedWidth="220px"
              />
              <PdsTextField
                label="JIRA ID"
                placeholder="RRP-001"
                value={filters.jiraId}
                onChange={(e) => setFilters({ ...filters, jiraId: e.target.value })}
                fixedWidth="130px"
              />
              <PdsTextField
                label="Created by"
                placeholder="Name or email…"
                value={filters.createdBy}
                onChange={(e) => setFilters({ ...filters, createdBy: e.target.value })}
                fixedWidth="200px"
              />
              <PdsBox direction="column" gap="xs" style={{ minWidth: 0, flex: '1 1 220px' }}>
                <PdsMantineText type="textline" component="span" className="argus-type-label">
                  Status
                </PdsMantineText>
                <PdsBox direction="row" gap="xs" wrap="wrap" alignItems="center">
                  {[
                    { value: '', label: 'All' },
                    { value: 'ACTIVE', label: 'Active' },
                    { value: 'PAUSED', label: 'Paused' },
                    { value: 'COMPLETED', label: 'Completed' },
                  ].map((opt) => (
                    <PdsMantineButton
                      key={opt.value || 'all'}
                      type="button"
                      rank={filters.status === opt.value ? 'fill' : 'outline'}
                      color="neutral"
                      size="sm"
                      onClick={() => handleStatusSegmentChange(opt.value)}
                      style={statusSegmentStyle(opt.value)}
                    >
                      {opt.label}
                    </PdsMantineButton>
                  ))}
                </PdsBox>
              </PdsBox>
            </PdsBox>
          </PdsBox>
          <PdsBox direction="row" justifyContent="flex-start" alignItems="center" wrap="wrap" gap="sm" style={{ marginTop: 12 }}>
            <PdsMantineButton type="submit" rank="fill" color="action" leadingIcon={<PdsFilterIcon />}>
              Apply Filters
            </PdsMantineButton>
            <PdsMantineButton type="button" rank="ghost" color="neutral" onClick={clearSearch} leadingIcon={<PdsCloseXIcon />}>
              Clear
            </PdsMantineButton>
          </PdsBox>
        </form>
      </PdsCard>

      {error && (
        <PdsNotification color="warning" onDismiss={() => setError(null)}>
          {error}
        </PdsNotification>
      )}

      {successMessage && (
        <PdsNotification color="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </PdsNotification>
      )}

      <GroupedSearchResults
        results={searchResults}
        loading={loading}
        navigate={navigate}
        onView={handleViewDetails}
        onEdit={handleEditCase}
        onClose={handleCloseCase}
        onPause={handlePauseCase}
        onResume={handleResumeCase}
        onDelete={handleDeleteCase}
        formatDate={formatDate}
        onPageChange={(page) => performSearch(filters, page)}
        onRefreshCases={refreshCaseList}
      />

      {/* Create Case Modal */}
      <PdsModal
        show={showCreateModal}
        onDismiss={() => setShowCreateModal(false)}
        minWidth="460px"
        maxWidth="560px"
        minHeight="auto"
        maxHeight="90vh"
      >
        <PdsModalHeader headline="Create New Case" />
        <PdsModalBody>
          <PdsBox direction="column" gap="md" style={{ width: '100%' }}>
            <PdsTextField
              label="URL/IP/Entity"
              placeholder="Domain, IP, or other target"
              value={newCase.target}
              onChange={(e) => setNewCase({ ...newCase, target: e.target.value })}
              required
              stretch
            />
            <PdsTextField
              label="Title"
              placeholder="Short title for this case..."
              value={newCase.title}
              onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
              stretch
            />
            <PdsTextArea
              label="Context"
              placeholder="Describe the case and node objectives..."
              value={newCase.description}
              onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
              rows={4}
              stretch
            />
            <PdsTextField
              label="JIRA Ticket (if relevant)"
              placeholder="RRP-001"
              value={newCase.jiraId}
              onChange={(e) => setNewCase({ ...newCase, jiraId: e.target.value })}
              stretch
            />
            {error && (
              <PdsNotification color="warning" dismissible={false}>
                {error}
              </PdsNotification>
            )}
          </PdsBox>
        </PdsModalBody>
        <PdsModalFooter>
          <PdsBox direction="row" justifyContent="end" gap="sm">
            <PdsMantineButton rank="outline" color="neutral" onClick={() => setShowCreateModal(false)} disabled={creating}>
              Cancel
            </PdsMantineButton>
            <PdsMantineButton
              rank="fill"
              color="action"
              onClick={handleCreateCase}
              disabled={creating}
              leadingIcon={creating ? <PdsSpinnerIcon /> : <PdsPlusAddIcon />}
            >
              {creating ? 'Creating...' : 'Create Case'}
            </PdsMantineButton>
          </PdsBox>
        </PdsModalFooter>
      </PdsModal>

      {/* Edit Case Modal */}
      {showEditModal && editingCase && (
        <EditCaseModal
          caseData={editingCase}
          onClose={() => { setShowEditModal(false); setEditingCase(null); }}
          onSuccess={(updatedCase) => {
            setShowEditModal(false);
            setEditingCase(null);
            performSearch(filters, currentPage);
            setSuccessMessage(`Case "${updatedCase.target}" has been updated successfully`);
            setTimeout(() => setSuccessMessage(null), 5000);
          }}
        />
      )}
    </PdsBox>
  );
};

// Edit Case Modal Component
const EditCaseModal = ({ caseData, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: caseData.title || '',
    description: caseData.description || '',
    target: caseData.target || '',
    jiraId: caseData.jiraId || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await axios.put(`/api/cases/${caseData.id}`, formData);
      onSuccess(response.data);
    } catch (err) {
      setError('Failed to update case: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PdsModal
      show
      onDismiss={onClose}
      minWidth="460px"
      maxWidth="560px"
      minHeight="auto"
      maxHeight="90vh"
    >
      <PdsModalHeader headline="Edit Case" />
      <PdsModalBody>
        <PdsBox direction="column" gap="md" style={{ width: '100%' }}>
          {error && (
            <PdsNotification color="warning" dismissible={false}>
              {error}
            </PdsNotification>
          )}
          <PdsTextField
            label="URL/IP/Entity"
            placeholder="Enter target domain or URL"
            value={formData.target}
            onChange={(e) => setFormData({ ...formData, target: e.target.value })}
            required
            stretch
          />
          <PdsTextField
            label="Title"
            placeholder="Short title for this case..."
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            stretch
          />
          <PdsTextArea
            label="Context"
            placeholder="Enter case context (optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            stretch
          />
          <PdsTextField
            label="JIRA Ticket (if relevant)"
            placeholder="RRP-001"
            value={formData.jiraId}
            onChange={(e) => setFormData({ ...formData, jiraId: e.target.value })}
            stretch
          />
        </PdsBox>
      </PdsModalBody>
      <PdsModalFooter>
        <PdsBox direction="row" justifyContent="end" gap="sm">
          <PdsMantineButton rank="outline" color="neutral" onClick={onClose} disabled={submitting}>
            Cancel
          </PdsMantineButton>
          <PdsMantineButton
            rank="fill"
            color="action"
            onClick={handleSubmit}
            disabled={submitting || !formData.target.trim()}
            leadingIcon={submitting ? <PdsSpinnerIcon /> : <PdsCheckConfirmIcon />}
          >
            {submitting ? 'Updating...' : 'Update Case'}
          </PdsMantineButton>
        </PdsBox>
      </PdsModalFooter>
    </PdsModal>
  );
};

// ─── Grouped Search Results ───────────────────────────────────────────────────

const GroupedSearchResults = ({ results, loading, navigate, onView, onEdit, onClose, onPause, onResume, onDelete, formatDate, onPageChange, onRefreshCases }) => {
  const [expandedCaseIds, setExpandedCaseIds] = useState(() => new Set());

  const toggleSubCases = (caseId) => {
    setExpandedCaseIds((prev) => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };

  const items = results?.results || [];
  const total = results?.totalElements || 0;
  const totalPages = results?.totalPages || 1;
  const currentPage = results?.currentPage || 0;
  const searchTime = results?.searchTimeMs || 0;

  if (!results) return null;

  return (
    <PdsBox direction="column" gap="sm">
      <PdsBox direction="row" alignItems="center" gap="xs">
        <PdsSearchIcon color="neutral" size="sm" />
        <PdsMantineText type="textline" fontWeight="strong" component="span" className="argus-type-ui-interactive">
          {total} result{total !== 1 ? 's' : ''}
        </PdsMantineText>
        <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
          ({searchTime}ms)
        </PdsMantineText>
        {loading && <PdsLoader size="sm" />}
      </PdsBox>

      {items.length === 0 ? (
        <PdsBox justifyContent="center" alignItems="center" direction="column" gap="sm" padding="xl">
          <PdsMantineText type="body" color="neutral" textAlign="center">
            No cases found matching your filters.
          </PdsMantineText>
        </PdsBox>
      ) : (
        items.map((result) => {
          const subCount = result.investigations?.length ?? 0;
          const hasSubCases = subCount > 0;
          const subExpanded = expandedCaseIds.has(result.id);
          return (
          <PdsBox key={result.id} direction="column" gap="xs">
            <PdsCard border borderRadius="md" padding="md">
              <div
                className="cases-search-result-card"
                onClick={(e) => {
                  if (isCaseCardStopClick(e.target)) return;
                  onView(result);
                }}
              >
              <PdsBox className="cases-search-result-details-row" style={{ minWidth: 0 }}>
                {hasSubCases ? (
                  <button
                    type="button"
                    className="cases-sub-expand-btn"
                    data-case-card-stop=""
                    aria-expanded={subExpanded}
                    aria-label={subExpanded ? 'Collapse nodes under this case' : `Expand ${subCount} node${subCount === 1 ? '' : 's'} under this case`}
                    onClick={() => toggleSubCases(result.id)}
                  />
                ) : (
                  <span className="cases-search-expand-cell-spacer" aria-hidden />
                )}
                <PdsBox direction="column" gap="xs" style={{ minWidth: 0, flex: 1 }}>
                {result.title && (
                  <PdsMantineText type="textline" fontWeight="strong">{result.title}</PdsMantineText>
                )}
                <PdsMantineText
                  type="textline"
                  color={result.title ? 'neutral' : undefined}
                  fontWeight={result.title ? undefined : 'strong'}
                  className="argus-data"
                >
                  {result.target}
                </PdsMantineText>
                {result.description && (
                  <PdsMantineText type="body" color="neutral">{result.description}</PdsMantineText>
                )}
                {result.jiraId && (
                  <PdsBox direction="row" alignItems="center" gap="xs" wrap="wrap" data-case-card-stop="">
                    <PdsTicketIcon color="action" size="sm" />
                    <PdsLink
                      href={`${JIRA_BROWSE_BASE}${result.jiraId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {result.jiraId} <PdsExternalLinkIcon size="xs" />
                    </PdsLink>
                  </PdsBox>
                )}
                {result.completedAt && (
                  <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                    Completed: {formatDate(result.completedAt)}
                  </PdsMantineText>
                )}
                </PdsBox>
              </PdsBox>
              <PdsBox direction="column" alignItems="flex-start" gap="xs" className="cases-search-result-col">
                <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-label">
                  Created
                </PdsMantineText>
                <PdsMantineText type="textline" component="span" className="argus-type-meta">
                  {formatDate(result.createdAt)}
                </PdsMantineText>
                {result.createdBy && (
                  <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                    {displayUsername(result.createdBy)}
                  </PdsMantineText>
                )}
              </PdsBox>
              <PdsBox direction="column" alignItems="flex-start" gap="xs" className="cases-search-result-col">
                <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-label">
                  Last updated
                </PdsMantineText>
                <PdsMantineText type="textline" component="span" className="argus-type-meta">
                  {result.updatedAt ? formatDate(result.updatedAt) : '—'}
                </PdsMantineText>
                <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                  {result.updatedBy ? displayUsername(result.updatedBy) : '—'}
                </PdsMantineText>
              </PdsBox>
              <PdsBox direction="column" alignItems="flex-start" gap="xs" className="cases-search-result-col">
                <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-label">
                  Status
                </PdsMantineText>
                <PdsTag color={STATUS_COLOR[result.status] || 'neutral'} dismissible={false} size="sm">
                  {result.status}
                </PdsTag>
              </PdsBox>
              <PdsBox direction="row" gap="xs" alignItems="center" justifyContent="flex-end" wrap="nowrap" className="cases-search-result-actions" data-case-card-stop="">
                <PdsMantineButton
                  type="button"
                  rank="ghost"
                  color="neutral"
                  size="sm"
                  leadingIcon={<PdsEyePublicViewIcon />}
                  data-case-card-stop=""
                  onClick={(e) => {
                    e.stopPropagation();
                    onView(result);
                  }}
                >
                  View
                </PdsMantineButton>
                <CaseCompactActions caseRow={result} onRefreshList={onRefreshCases} />
                {result.status === 'PAUSED' && (
                  <PdsMantineButton
                    data-case-card-stop=""
                    rank="ghost"
                    color="action"
                    size="sm"
                    onClick={() => onResume(result)}
                    leadingIcon={<PdsCheckConfirmIcon />}
                  >
                    Resume
                  </PdsMantineButton>
                )}
              </PdsBox>
              </div>
            </PdsCard>

            {hasSubCases && subExpanded && (
              <PdsBox direction="column" gap="xs" style={{ paddingLeft: 8 }}>
                {result.investigations.map((inv) => (
                  <PdsCard
                    key={inv.id}
                    border
                    borderRadius="sm"
                    padding="sm"
                    color="neutral"
                    contrast="low"
                    className="cases-investigation-node-card"
                    role="button"
                    tabIndex={0}
                    title={`Open case and focus ${inv.targetValue} on the map`}
                    onClick={() => onView(result, inv.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onView(result, inv.id);
                      }
                    }}
                  >
                    <PdsBox direction="column" gap="sm" style={{ width: '100%' }}>
                      <PdsBox direction="row" alignItems="center" gap="xs" wrap="wrap">
                        <PdsMantineText type="textline" component="span" className="argus-data">
                          {inv.targetValue}
                        </PdsMantineText>
                        {inv.deriveFrom && (
                          <PdsTag color="neutral" dismissible={false} size="xs">
                            {formatDeriveFromLabel(inv.deriveFrom)}
                          </PdsTag>
                        )}
                      </PdsBox>
                      {(inv.findingsPreview?.length > 0) ? (
                        <PdsBox direction="column" gap="sm">
                          {(inv.findingsPreview || []).map((f, idx) => (
                            <PdsBox
                              key={`${inv.id}-f-${idx}`}
                              direction="column"
                              gap={4}
                              style={{ paddingLeft: 10, borderLeft: '2px solid #d0d7de' }}
                            >
                              <PdsTag color="action" dismissible={false} size="xs">{f.categoryLabel}</PdsTag>
                              <PdsMantineText
                                type="textline"
                                color="neutral"
                                component="span"
                                className="argus-data"
                                style={{ wordBreak: 'break-word' }}
                              >
                                {f.informationValue}
                              </PdsMantineText>
                              {f.remarks && (
                                <PdsMantineText
                                  type="textline"
                                  color="neutral"
                                  component="span"
                                  className="argus-type-row-meta"
                                  style={{ wordBreak: 'break-word', fontStyle: 'italic' }}
                                >
                                  {f.remarks}
                                </PdsMantineText>
                              )}
                            </PdsBox>
                          ))}
                          {(() => {
                            const fc = inv.findingsCount ?? 0;
                            const fp = inv.findingsPreview?.length ?? 0;
                            const more = fc - fp;
                            return more > 0 ? (
                              <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                                +{more} more finding{more === 1 ? '' : 's'}
                              </PdsMantineText>
                            ) : null;
                          })()}
                        </PdsBox>
                      ) : (inv.findingsCount ?? 0) > 0 ? (
                        <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                          {inv.findingsCount} finding{inv.findingsCount === 1 ? '' : 's'} on this node — open the case for full details.
                        </PdsMantineText>
                      ) : (
                        <PdsMantineText
                          type="textline"
                          color="neutral"
                          component="span"
                          className="argus-type-meta"
                          style={{ fontStyle: 'italic' }}
                        >
                          No findings recorded for this node yet.
                        </PdsMantineText>
                      )}
                    </PdsBox>
                  </PdsCard>
                ))}
              </PdsBox>
            )}
          </PdsBox>
          );
        })
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage + 1}
          totalItems={total}
          itemsPerPage={Math.max(1, Math.ceil(total / totalPages))}
          onPageChange={(page) => onPageChange(page - 1)}
        />
      )}
    </PdsBox>
  );
};

export default Cases;
